import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../utils/api';
import { Plus, Search, Filter, Download, RefreshCw, Wrench, AlertCircle, Cpu, ChevronDown } from 'lucide-react';

export default function WorkOrders() {
  const navigate = useNavigate();
  const [workOrders, setWorkOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filters, setFilters] = useState({ status: '', priority: '', department: '', source: '' });
  const [showCreate, setShowCreate] = useState(false);
  const [components, setComponents] = useState([]);
  const [generating, setGenerating] = useState(false);

  useEffect(() => { loadData(); }, [filters]);

  const loadData = async () => {
    setLoading(true);
    try {
      const params = {};
      if (filters.status) params.status = filters.status;
      if (filters.priority) params.priority = filters.priority;
      if (filters.department) params.department = filters.department;
      if (filters.source) params.source = filters.source;
      const res = await api.get('/work-orders', { params });
      setWorkOrders(res.data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const generatePeriodic = async () => {
    setGenerating(true);
    try {
      await api.post('/work-orders/generate-periodic');
      loadData();
    } catch (e) { console.error(e); }
    finally { setGenerating(false); }
  };

  const filtered = workOrders.filter(wo =>
    !search || wo.job_title?.toLowerCase().includes(search.toLowerCase()) || wo.wo_number?.toLowerCase().includes(search.toLowerCase())
  );

  const counts = {
    Open: workOrders.filter(w => w.status === 'Open').length,
    'In Progress': workOrders.filter(w => w.status === 'In Progress').length,
    'Pending Close': workOrders.filter(w => w.status === 'Pending Close').length,
    Deferred: workOrders.filter(w => w.status === 'Deferred').length,
    Closed: workOrders.filter(w => w.status === 'Closed').length,
    AI: workOrders.filter(w => w.source === 'AI').length,
  };

  return (
    <div>
      {/* Quick filters */}
      <div style={{ display: 'flex', gap: 6, marginBottom: 16, flexWrap: 'wrap' }}>
        {[
          { key: '', label: `All (${workOrders.length})` },
          { key: 'Open', label: `Open (${counts.Open})`, color: 'var(--info)' },
          { key: 'In Progress', label: `In Progress (${counts['In Progress']})`, color: 'var(--warning)' },
          { key: 'Pending Close', label: `Pending Close (${counts['Pending Close']})`, color: '#f59e0b' },
          { key: 'Deferred', label: `Deferred (${counts.Deferred})`, color: '#7c3aed' },
          { key: 'Closed', label: `Closed (${counts.Closed})`, color: 'var(--success)' },
          { key: 'AI', label: `AI (${counts.AI})`, color: '#7c3aed' },
        ].map(f => (
          <button
            key={f.key}
            className={`btn btn-sm ${filters.source === f.key ? 'btn-primary' : 'btn-outline'}`}
            onClick={() => setFilters(f.key === 'AI' ? { ...filters, source: 'AI' } : { ...filters, source: '', status: f.key })}
            style={filters.status === f.key || (f.key === '' && !filters.status && !filters.source) ? { background: 'var(--primary)', color: 'white', borderColor: 'var(--primary)' } : {}}
          >
            {f.label}
          </button>
        ))}
      </div>

      {/* Action bar */}
      <div className="action-bar">
        <div className="search-bar" style={{ flex: 1, maxWidth: 400 }}>
          <input className="form-input" placeholder="Search work orders..." value={search} onChange={e => setSearch(e.target.value)} />
          <button className="btn btn-outline"><Search size={15} /></button>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <select className="form-select" style={{ minWidth: 130 }} value={filters.department} onChange={e => setFilters({ ...filters, department: e.target.value })}>
            <option value="">All Depts</option>
            <option value="Engine">Engine</option>
            <option value="Deck">Deck</option>
            <option value="Electrical">Electrical</option>
            <option value="Safety">Safety</option>
          </select>
          <select className="form-select" style={{ minWidth: 130 }} value={filters.priority} onChange={e => setFilters({ ...filters, priority: e.target.value })}>
            <option value="">All Priority</option>
            <option value="High">High</option>
            <option value="Medium">Medium</option>
            <option value="Low">Low</option>
          </select>
          <button className="btn btn-outline" onClick={generatePeriodic} disabled={generating}>
            <Wrench size={15} /> {generating ? 'Generating...' : 'Generate Periodic WOs'}
          </button>
          <button className="btn btn-accent" onClick={() => setShowCreate(true)}>
            <Plus size={15} /> New Work Order
          </button>
        </div>
      </div>

      {/* Table */}
      <div className="card">
        {loading ? (
          <div className="card-body">
            {[...Array(5)].map((_, i) => <div key={i} className="loading-row"><div style={{ flex: 1, height: 16, background: '#eee', borderRadius: 4 }}></div></div>)}
          </div>
        ) : filtered.length === 0 ? (
          <div className="empty-state"><Wrench /><h4>No work orders found</h4><p>Try adjusting your filters or create a new work order.</p></div>
        ) : (
          <div className="table-container">
            <table>
              <thead>
                <tr>
                  <th>WO #</th><th>Job Title</th><th>Equipment</th><th>Dept</th><th>Due Date</th>
                  <th>Priority</th><th>Assigned</th><th>Source</th><th>Status</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map(wo => {
                  const isOverdue = wo.due_date && new Date(wo.due_date) < new Date() && !['Closed','Rejected'].includes(wo.status);
                  return (
                    <tr key={wo.id} className={`priority-${wo.priority.toLowerCase()}`} onClick={() => navigate(`/work-orders/${wo.id}`)} style={{ cursor: 'pointer' }}>
                      <td>
                        {wo.source === 'AI' ? <span className="badge badge-ai">{wo.wo_number}</span> : <span>{wo.wo_number}</span>}
                      </td>
                      <td>
                        <div style={{ fontWeight: 500 }}>{wo.job_title}</div>
                        {wo.ai_health_score && <span style={{ fontSize: 11, color: 'var(--text-secondary)' }}>AI Health: {wo.ai_health_score} | {wo.ai_confidence}% conf.</span>}
                      </td>
                      <td>{wo.component_name || '—'}</td>
                      <td>{wo.department}</td>
                      <td style={{ color: isOverdue ? 'var(--danger)' : 'var(--text)', fontWeight: isOverdue ? 600 : 400 }}>
                        {wo.due_date || '—'}
                        {isOverdue && <span style={{ fontSize: 10, display: 'block', color: 'var(--danger)' }}>OVERDUE</span>}
                      </td>
                      <td><span className={`badge badge-${wo.priority.toLowerCase()}`}>{wo.priority}</span></td>
                      <td><span style={{ fontSize: 12 }}>{wo.assigned_to}</span></td>
                      <td>
                        <span className={`badge ${wo.source === 'AI' ? 'badge-ai' : wo.source === 'Non-Periodic' ? 'badge-warning' : 'badge-draft'}`}>
                          {wo.source}
                        </span>
                      </td>
                      <td><span className={`badge badge-${wo.status.toLowerCase().replace(' ', '')}`}>{wo.status}</span></td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Create Modal */}
      {showCreate && <CreateWOModal onClose={() => setShowCreate(false)} onCreated={loadData} />}
    </div>
  );
}

function CreateWOModal({ onClose, onCreated }) {
  const [form, setForm] = useState({
    source: 'Non-Periodic',
    job_title: '',
    component_id: '',
    department: '',
    wo_type: 'One-Off',
    problem_description: '',
    priority: 'Medium',
    assigned_to: '',
    due_date: '',
  });
  const [components, setComponents] = useState([]);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    api.get('/inventory/components').then(r => setComponents(r.data)).catch(console.error);
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post('/work-orders', form);
      onCreated();
      onClose();
    } catch (e) { console.error(e); }
    finally { setSubmitting(false); }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal modal-lg" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Create Non-Periodic Work Order</h2>
          <button className="btn btn-outline btn-sm" onClick={onClose}>×</button>
        </div>
        <form onSubmit={handleSubmit}>
          <div className="modal-body">
            <div className="form-row">
              <div className="form-group">
                <label className="form-label">WO Source *</label>
                <select className="form-select" value={form.source} onChange={e => setForm({ ...form, source: e.target.value, wo_type: e.target.value === 'Non-Periodic' ? 'One-Off' : '' })}>
                  <option value="Non-Periodic">Non-Periodic</option>
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">WO Type *</label>
                <select className="form-select" value={form.wo_type} onChange={e => setForm({ ...form, wo_type: e.target.value })}>
                  <option value="One-Off">One-Off Job</option>
                  <option value="Breakdown">Breakdown Repair</option>
                  <option value="Defect">Defect Rectification</option>
                  <option value="Superintendent Order">Superintendent Order</option>
                  <option value="Class Finding">Class / Survey Finding</option>
                </select>
              </div>
            </div>
            <div className="form-group">
              <label className="form-label">Job Title *</label>
              <input className="form-input" value={form.job_title} onChange={e => setForm({ ...form, job_title: e.target.value })} placeholder="Describe the job" required />
            </div>
            <div className="form-row">
              <div className="form-group">
                <label className="form-label">Equipment</label>
                <select className="form-select" value={form.component_id} onChange={e => setForm({ ...form, component_id: e.target.value })}>
                  <option value="">Select equipment...</option>
                  {components.map(c => <option key={c.id} value={c.id}>{c.component_name} ({c.department})</option>)}
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">Department</label>
                <select className="form-select" value={form.department} onChange={e => setForm({ ...form, department: e.target.value })}>
                  <option value="">Select...</option>
                  <option value="Engine">Engine</option>
                  <option value="Deck">Deck</option>
                  <option value="Electrical">Electrical</option>
                  <option value="Safety">Safety</option>
                  <option value="Hull">Hull</option>
                </select>
              </div>
            </div>
            <div className="form-row">
              <div className="form-group">
                <label className="form-label">Priority *</label>
                <select className="form-select" value={form.priority} onChange={e => setForm({ ...form, priority: e.target.value })}>
                  <option value="High">High</option>
                  <option value="Medium">Medium</option>
                  <option value="Low">Low</option>
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">Assigned To</label>
                <select className="form-select" value={form.assigned_to} onChange={e => setForm({ ...form, assigned_to: e.target.value })}>
                  <option value="">Select...</option>
                  <option value="chief_engineer">Chief Engineer</option>
                  <option value="second_engineer">2nd Engineer</option>
                  <option value="third_engineer">3rd Engineer</option>
                  <option value="electrical_officer">Electrical Officer</option>
                  <option value="bosun">Bosun</option>
                </select>
              </div>
            </div>
            <div className="form-group">
              <label className="form-label">Due Date</label>
              <input className="form-input" type="date" value={form.due_date} onChange={e => setForm({ ...form, due_date: e.target.value })} />
            </div>
            <div className="form-group">
              <label className="form-label">Problem Description *</label>
              <textarea className="form-textarea" value={form.problem_description} onChange={e => setForm({ ...form, problem_description: e.target.value })} placeholder="What happened or what was observed" required />
            </div>
          </div>
          <div className="modal-footer">
            <button type="button" className="btn btn-outline" onClick={onClose}>Cancel</button>
            <button type="submit" className="btn btn-primary" disabled={submitting}>{submitting ? 'Creating...' : 'Create Work Order'}</button>
          </div>
        </form>
      </div>
    </div>
  );
}
